import { ensureArray, loadScript, once } from '@mbrtargeting/metatag-utils';
import { simpleParamsSerializer } from '../../../../src/utils/url-helper.js';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onPhase, Phase } from '../../../loader/decorators/on-phase.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectModuleSettings } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { ConsentCache } from '../../../loader/modules/consent-cache.js';
import { CONFIG_RESOLVER, CONSENT_CACHE } from '../../../loader/token.js';
import { GLOBAL_TARGETING, ID_STORE, STORAGE_HELPER } from '../../token.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';
import { IdStore } from '../idstore/idstore.js';
import { StorageConsent, StorageHelper, StorageType } from '../storage/storage-helper.js';

const log = logger.logGroup({ prefix: 'AudienceProject' });

declare var window: Window & yieldlove.Window & {
    audienceProjectLayer?: Array<[cmd: 'setUserId', idName: string, idValue: string]>;
    apDataKeyValues?: AudienceProjectKeyValues;
};

export interface AudienceProjectKeyValues {
    [key: string]: string | string[];
};

export interface AudienceProjectLocalStorageEntry {
    ttl: number;
    data: AudienceProjectKeyValues;
}

@injectionTarget()
export class AudienceProject {

    @inject(CONFIG_RESOLVER) #configResolver!: ConfigResolver;
    @inject(ID_STORE) #idStore!: IdStore;
    @inject(CONSENT_CACHE) #consentCache!: ConsentCache;
    @inject(STORAGE_HELPER) #storageHelper!: StorageHelper;
    @inject(GLOBAL_TARGETING) #globalTargeting!: GlobalTargeting;

    STORAGE_CACHE_KEY = 'ap_data';

    @onPhase({ phase: Phase.TRACKING })
    async generateAudienceProjectPixel() {

        const { active = false, iabVendorId = 0, url } = this.#configResolver.get(selectModuleSettings('AUDIENCE_PROJECT')) || {};

        if (!active) return log.debug('module not active. Not generating AudienceProjectPixel');
        if (!url) return log.debug('url not set');

        const allowed = await this.validateConsent(iabVendorId);
        if (!allowed) return;

        window.audienceProjectLayer ||= [];

        const pubco = this.#idStore.getId("pubCommonId") || this.#idStore.getId("sharedid");
        const id5id = this.#idStore.getId("id5id");
        const adscaleid = this.#idStore.getId("adscaleid");

        pubco && window.audienceProjectLayer.push(["setUserId", "pubco", pubco]);
        id5id && window.audienceProjectLayer.push(["setUserId", "id5id", id5id]);
        adscaleid && window.audienceProjectLayer.push(["setUserId", "adscaleid", adscaleid]);

        log.notice('adding IDs to AudienceProjectLayer');

        loadScript({ src: url, node: document.head })
    }

    @onPhase({ phase: Phase.TARGETING })
    async retrieveTargetingData() {

        const { active = false, iabVendorId = 0, allowBusinessAdTargetings = false } = this.#configResolver.get(selectModuleSettings('AUDIENCE_PROJECT')) || {};

        if (!active) return log.debug('module not active. Not fetching BusinessAd Targeting');
        if (!allowBusinessAdTargetings) return log.debug('BusinessAd Targeting disabled');

        const allowed = await this.validateConsent(iabVendorId);
        if (!allowed) return log.debug('no consent');

        const keyValues = await this.recoverKeyValues();

        if (Object.entries(keyValues).length !== 0) {
            this.setTargetings(keyValues);
        } else {
            this.fetchKeyValues();
        }
    }

    /**
     * Try to recover keyValues from localStorage. Returns empty object if not found or expired.
     * @returns {Promise<AudienceProjectKeyValues>} recovered keyValues or empty object
     */
    private async recoverKeyValues(): Promise<AudienceProjectKeyValues> {
        let keyValues: AudienceProjectKeyValues = {};
        try {
            const data: string | null = await this.#storageHelper.get(StorageType.LOCAL, StorageConsent.WAIT, this.STORAGE_CACHE_KEY);
            const parsed: AudienceProjectLocalStorageEntry | null = data ? JSON.parse(data) : null;

            if (!parsed || Date.now() > Number(parsed['ttl'])) {
                this.#storageHelper.delete(StorageType.LOCAL, StorageConsent.WAIT, this.STORAGE_CACHE_KEY);
            } else {
                keyValues = parsed['data'];
                window.apDataKeyValues = keyValues; // not necessary for metatag, unsure if used by other scripts
            }
        } catch (e) {
            this.#storageHelper.delete(StorageType.LOCAL, StorageConsent.WAIT, this.STORAGE_CACHE_KEY);
        }
        return keyValues;
    }

    /**
     * Fetches keyValues from BusinessAd endpoint.
     * @param callback function to call with retrieved keyValues
     */
    private async fetchKeyValues() {

        const setTargetingOnce = once((keyValues: AudienceProjectKeyValues) => this.setTargetings(keyValues));

        const tcData = await this.#consentCache.getTcDataPromise();
        const gdprApplies = tcData?.gdprApplies ?? true;
        const gdpr_consent = tcData?.tcString || '';

        const ref = document.referrer;
        let sref = (~ref?.indexOf(window.location.origin)) ? ref : '';
        if (sref) this.#storageHelper.set(StorageType.LOCAL, StorageConsent.WAIT, 'apr_sref', sref);

        const queryParams = {
            med: window.location.toString(),
            dsu: await this.#storageHelper.get(StorageType.LOCAL, StorageConsent.WAIT, 'apr_dsu') || '',
            ref,
            sref,
            gdpr: String(gdprApplies),
            gdpr_consent,
        }

        let url = `https://pdw-usr.userreport.com/api/v2/partner/businessad/uid?${simpleParamsSerializer(queryParams)}`;

        fetch(url, { credentials: 'include' })
            .then(response => {
                return response.json();
            })
            .then(json => {
                window.apDataKeyValues = json.keyValues ?? {}; // not necessary for metatag, unsure if used by other scripts
                setTargetingOnce(json.keyValues ?? {});
            })
            .catch(() => {
                log.error('Error fetching keyValues from BusinessAd Endpoint')
                setTargetingOnce({});
            });
    }

    /**
     * Adds targetings to GlobalTargeting.
     */
    private setTargetings(keyValues: AudienceProjectKeyValues) {
        for (const key in keyValues) {
            this.#globalTargeting.addKeyValues({ [key]: ensureArray(keyValues[key]) });
        }
        log.info('Added AudienceProject keyValues to GlobalTargeting');
        this.storeKeyValues(keyValues);
    }

    /**
     * Saves retrieved keyValues to localStorage with a Expiry Date 3 days from now.
     * @param keyValues
     */
    private storeKeyValues(keyValues: AudienceProjectKeyValues): void {
        const ap_data: AudienceProjectLocalStorageEntry = {
            'ttl': Date.now() + (3 * 24 * 60 * 60 * 1000),
            'data': keyValues,
        };
        this.#storageHelper.set(StorageType.LOCAL, StorageConsent.WAIT, this.STORAGE_CACHE_KEY, JSON.stringify(ap_data));
    }

    /**
     * Check consent for given IAB vendor ID
     * @param iabVendorId
     * @returns true if consent for vendor is given
     */
    private validateConsent = async (iabVendorId: number): Promise<boolean> => {
        const allowed = await this.#consentCache.whenConsentConditions([
            { consentedVendors: [iabVendorId] },
            { legitimateInterestVendors: [iabVendorId] },
            { gdprApplies: false }
        ]);
        return allowed;
    }
}
